/**
 * AGC: Staking Dashboard Component for Origami Battle 2.0
 * AGC: Full-screen design with revenue sharing information
 * @author AGC (AI-Generated Code) with Cursor
 */

import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useAccount, useReadContract, useWriteContract, useWaitForTransactionReceipt, usePublicClient } from 'wagmi'
import { formatEther, formatUnits } from 'viem'
import { parseAbi } from 'viem'
import { useStaking } from '../hooks/useStaking'
import { Shield, ShoppingBag, TrendingUp, Zap } from 'lucide-react'
import { buildBackendUrl } from '../utils/backendUrl'
import { playSoundSafe } from '../utils/audio'

interface UnstakeRequestData {
  amount: bigint
  requestedAt: bigint
  unlockTime: bigint
  completed: boolean
  index: number
}

// AGC: Contract addresses from environment (env.public)
// AGC: CRITICAL: Always use environment variables - never hardcode addresses!
// AGC: When contract changes, update env.public and restart dev server (clear Vite cache)
const STAKING_MANAGER_ADDRESS = (import.meta.env.VITE_STAKING_MANAGER_ADDRESS || '0x0000000000000000000000000000000000000000') as `0x${string}`
const STAKING_REVENUE_SHARE_ADDRESS = (import.meta.env.VITE_STAKING_REVENUE_SHARE_ADDRESS || '0x0000000000000000000000000000000000000000') as `0x${string}`
const BUFF1155_ADDRESS = (import.meta.env.VITE_BUFF1155_ADDRESS || '0x0000000000000000000000000000000000000000') as `0x${string}`

// AGC: Debug log to verify environment variables are loaded correctly
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  console.log('🔍 [StakingDashboard] Contract addresses from env.public:', {
    STAKING_MANAGER: STAKING_MANAGER_ADDRESS,
    STAKING_REVENUE_SHARE: STAKING_REVENUE_SHARE_ADDRESS,
    BUFF1155: BUFF1155_ADDRESS,
    envCheck: {
      VITE_STAKING_MANAGER_ADDRESS: import.meta.env.VITE_STAKING_MANAGER_ADDRESS,
      VITE_STAKING_REVENUE_SHARE_ADDRESS: import.meta.env.VITE_STAKING_REVENUE_SHARE_ADDRESS,
      VITE_BUFF1155_ADDRESS: import.meta.env.VITE_BUFF1155_ADDRESS,
    }
  })
}

// AGC: Week epoch from StakingRevenueShare contract (Monday, Jan 1, 2024 00:00:00 UTC)
const WEEK_EPOCH = 1704067200

// AGC: Contract ABIs
// AGC: Note: totalStaked is calculated from StakingManager's OGB balance

const STAKING_REVENUE_SHARE_ABI = parseAbi([
  'function getCurrentWeek() public view returns (uint256)',
  'function getWeekRevenue(uint256 week) external view returns (uint256 ethAmount, uint256 ogbAmount, uint256 usdcAmount, bool finalized)',
  'function getUserSharePercentage(address user, uint256 week) external view returns (uint256)',
  'function getPendingRewards(address user, uint256 week) external view returns (uint256 ethAmount, uint256 ogbAmount, uint256 usdcAmount)',
  'function claimRewards(uint256 week) external',
])

const BUFF1155_ABI = parseAbi([
  'function assetQuotes() view returns (uint256 ogbPerUsdMicro, uint256 ethPerUsdMicro, uint256 usdcPerUsdMicro, uint256 updatedAt)',
])

// AGC: StakingManager ABI for on-chain unstake requests
const STAKING_MANAGER_ABI = parseAbi([
  'function getStakingInfo(address user) external view returns (uint256 stakedBalance, uint256 stakingScore, uint8 tier, uint256 pendingUnstakes)',
  'function getUnstakeRequest(address user, uint256 requestIndex) external view returns (uint256 amount, uint256 requestedAt, uint256 unlockTime, bool completed)',
  'function getStakedBalance(address user) external view returns (uint256)',
])

export const StakingDashboard: React.FC = () => {
  const { address, isConnected } = useAccount()
  const publicClient = usePublicClient()
  const {
    stakingInfo,
    ogbBalance,
    stake,
    requestUnstake,
    completeUnstake,
    refetchStakingInfo,
    isPending,
    stakingManagerAddress,
    error: stakingHookError,
    lockInfo, // AGC: V5+ airdrop lock info
  } = useStaking(address)

  const [stakeAmount, setStakeAmount] = useState('')
  const [unstakeAmount, setUnstakeAmount] = useState('')
  const [unstakeRequests, setUnstakeRequests] = useState<UnstakeRequestData[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'stake' | 'unstake'>('stake')
  const [claimTxHash, setClaimTxHash] = useState<`0x${string}` | null>(null)
  const [pendingRewards, setPendingRewards] = useState<{ eth: bigint; ogb: bigint; usdc: bigint } | null>(null)
  const [weekFinalized, setWeekFinalized] = useState<boolean>(false)
  const [showNextPaymentTooltip, setShowNextPaymentTooltip] = useState<boolean>(false)
  
  // AGC: Coin sound interval ref for claim animation
  const coinSoundIntervalRef = useRef<NodeJS.Timeout | null>(null)
  
  const { writeContractAsync: writeContract, isPending: isClaimPending } = useWriteContract()
  const { data: claimReceipt } = useWaitForTransactionReceipt({
    hash: claimTxHash || undefined,
  })
  
  // AGC: Revenue share data
  const [totalStaked, setTotalStaked] = useState<bigint>(0n)
  const [totalRevenueUSDC, setTotalRevenueUSDC] = useState<number>(0) // AGC: Changed from totalEarningUSDC to totalRevenueUSDC
  const [myEarningUSDC, setMyEarningUSDC] = useState<number>(0)
  const [allPurchases, setAllPurchases] = useState<Array<{
    price: bigint
    paymentMethod: string
    timestamp: number
  }>>([])

  // AGC: Read total staked from StakingManager (OGB balance of contract = total staked)
  const OGB_TOKEN_ADDRESS = (import.meta.env.VITE_OGB_TOKEN_ADDRESS || '0x0000000000000000000000000000000000000000') as `0x${string}`
  const OGB_TOKEN_ABI = parseAbi(['function balanceOf(address account) external view returns (uint256)'])
  
  const { data: totalStakedData, refetch: refetchTotalStaked } = useReadContract({
    address: OGB_TOKEN_ADDRESS,
    abi: OGB_TOKEN_ABI,
    functionName: 'balanceOf',
    args: STAKING_MANAGER_ADDRESS !== '0x0000000000000000000000000000000000000000' ? [STAKING_MANAGER_ADDRESS] : undefined,
    query: {
      enabled: isConnected && STAKING_MANAGER_ADDRESS !== '0x0000000000000000000000000000000000000000',
    },
  })

  // AGC: Read current week from RevenueShare
  const { data: currentWeek, refetch: refetchCurrentWeek } = useReadContract({
    address: STAKING_REVENUE_SHARE_ADDRESS,
    abi: STAKING_REVENUE_SHARE_ABI,
    functionName: 'getCurrentWeek',
    query: {
      enabled: isConnected && STAKING_REVENUE_SHARE_ADDRESS !== '0x0000000000000000000000000000000000000000',
    },
  })

  // AGC: Calculate next payment date/time (Sunday 23:59 UTC of current week)
  // AGC: Using useMemo to calculate when currentWeek is available
  const nextPaymentDate = React.useMemo((): Date | null => {
    if (currentWeek === undefined) return null
    
    // AGC: WEEK_EPOCH = 1704067200 (Monday, Jan 1, 2024 00:00:00 UTC)
    // AGC: Week duration = 7 days
    // AGC: Current week starts at: WEEK_EPOCH + (currentWeek * 7 days)
    // AGC: Week ends at: (WEEK_EPOCH + ((currentWeek + 1) * 7 days)) - 1 second
    // AGC: Next payment = Sunday 23:59:59 UTC of current week
    
    const WEEK_EPOCH = 1704067200 // Monday, Jan 1, 2024 00:00:00 UTC
    const WEEK_DURATION = 7 * 24 * 60 * 60 // 7 days in seconds
    
    // AGC: Calculate end of current week (Sunday 23:59:59 UTC)
    const weekStart = Number(WEEK_EPOCH) + (Number(currentWeek) * WEEK_DURATION)
    const weekEnd = weekStart + WEEK_DURATION - 1 // Sunday 23:59:59 UTC
    
    // AGC: If current time is past week end, next payment is end of next week
    const now = Math.floor(Date.now() / 1000)
    const nextPaymentTimestamp = now > weekEnd ? weekEnd + WEEK_DURATION : weekEnd
    
    return new Date(nextPaymentTimestamp * 1000)
  }, [currentWeek])
  
  const nextPaymentDateStr = nextPaymentDate 
    ? nextPaymentDate.toLocaleString('en-US', { 
        timeZone: 'UTC', 
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short'
      })
    : 'Calculating...'

  // AGC: Read week revenue
  const { data: weekRevenueData, refetch: refetchWeekRevenue } = useReadContract({
    address: STAKING_REVENUE_SHARE_ADDRESS,
    abi: STAKING_REVENUE_SHARE_ABI,
    functionName: 'getWeekRevenue',
    args: currentWeek !== undefined ? [currentWeek] : undefined,
    query: {
      enabled: isConnected && currentWeek !== undefined && STAKING_REVENUE_SHARE_ADDRESS !== '0x0000000000000000000000000000000000000000',
    },
  })

  // AGC: Read my share percentage
  const { data: mySharePercentData, refetch: refetchMyShare } = useReadContract({
    address: STAKING_REVENUE_SHARE_ADDRESS,
    abi: STAKING_REVENUE_SHARE_ABI,
    functionName: 'getUserSharePercentage',
    args: address && currentWeek !== undefined ? [address, currentWeek] : undefined,
    query: {
      enabled: isConnected && !!address && currentWeek !== undefined && STAKING_REVENUE_SHARE_ADDRESS !== '0x0000000000000000000000000000000000000000',
    },
  })

  // AGC: Check if current week is finalized (FIX: Set directly from weekRevenueData to break circular dependency)
  const isCurrentWeekFinalized = weekRevenueData && Array.isArray(weekRevenueData) 
    ? (weekRevenueData as [bigint, bigint, bigint, boolean])[3] 
    : false

  // AGC: Calculate claimable week (always claim from previous week if it's finalized)
  // AGC: FIX 2025-12-01: Always claim from previous week, not current week
  // AGC: Current week shouldn't be claimable until it ends and becomes previous week
  const claimableWeek = currentWeek !== undefined && currentWeek > 0n
    ? currentWeek - 1n
    : undefined

  // AGC: Read pending rewards for claimable week
  const { data: pendingRewardsData, refetch: refetchPendingRewards } = useReadContract({
    address: STAKING_REVENUE_SHARE_ADDRESS,
    abi: STAKING_REVENUE_SHARE_ABI,
    functionName: 'getPendingRewards',
    args: address && claimableWeek !== undefined ? [address, claimableWeek] : undefined,
    query: {
      enabled: isConnected && !!address && claimableWeek !== undefined && STAKING_REVENUE_SHARE_ADDRESS !== '0x0000000000000000000000000000000000000000',
    },
  })

  // AGC: Read previous week revenue to check if finalized (always needed since claimableWeek is previous week)
  const { data: previousWeekRevenueData } = useReadContract({
    address: STAKING_REVENUE_SHARE_ADDRESS,
    abi: STAKING_REVENUE_SHARE_ABI,
    functionName: 'getWeekRevenue',
    args: claimableWeek !== undefined ? [claimableWeek] : undefined,
    query: {
      enabled: isConnected && claimableWeek !== undefined && STAKING_REVENUE_SHARE_ADDRESS !== '0x0000000000000000000000000000000000000000',
    },
  })

  // AGC: Read asset quotes from Buff1155
  const { data: assetQuotesData, refetch: refetchAssetQuotes } = useReadContract({
    address: BUFF1155_ADDRESS,
    abi: BUFF1155_ABI,
    functionName: 'assetQuotes',
    query: {
      enabled: isConnected && BUFF1155_ADDRESS !== '0x0000000000000000000000000000000000000000',
    },
  })

  // AGC: Auto-refresh on page enter
  useEffect(() => {
    if (isConnected && address) {
      refetchStakingInfo()
      refetchTotalStaked()
      refetchCurrentWeek()
      refetchAssetQuotes()
      setLoading(false)
    }
  }, [isConnected, address])

  // AGC: Update revenue data when contracts update
  useEffect(() => {
    if (totalStakedData) {
      setTotalStaked(totalStakedData as bigint)
    }
  }, [totalStakedData])

  // AGC: Update weekFinalized state based on claimable week's finalized status
  // AGC: FIX 2025-12-01: claimableWeek is always previous week, so always check previousWeekRevenueData
  useEffect(() => {
    if (claimableWeek !== undefined && previousWeekRevenueData && Array.isArray(previousWeekRevenueData)) {
      // AGC: Check previous week's finalized status (claimableWeek is always previous week)
      const [, , , finalized] = previousWeekRevenueData as [bigint, bigint, bigint, boolean]
      setWeekFinalized(finalized)
      console.log(`🔍 [StakingDashboard] Week ${claimableWeek.toString()} finalized:`, finalized)
    } else {
      setWeekFinalized(false)
    }
  }, [previousWeekRevenueData, claimableWeek])

  // AGC: Calculate last Monday 00:00 UTC timestamp
  const getLastMondayTimestamp = useMemo(() => {
    const now = Math.floor(Date.now() / 1000) // Current timestamp in seconds
    const daysSinceEpoch = Math.floor((now - WEEK_EPOCH) / (24 * 60 * 60))
    const daysIntoWeek = daysSinceEpoch % 7
    const lastMonday = now - (daysIntoWeek * 24 * 60 * 60)
    return lastMonday
  }, [])

  // AGC: Fetch ALL purchase events from last Monday 00:00 UTC to now
  // AGC: Uses backend API with Firebase caching (incremental sync)
  // AGC: Much faster than on-chain queries and avoids RPC block range limits
  useEffect(() => {
    if (!isConnected) {
      setAllPurchases([])
      return
    }

    const fetchAllPurchases = async () => {
      try {
        const lastMondayTimestamp = getLastMondayTimestamp
        const fromTimestamp = lastMondayTimestamp * 1000 // AGC: Convert to milliseconds

        // AGC: Call backend API with useCache=true to prevent spam syncing (syncs max once per 60 minutes)
        // AGC: StakingDashboard polls every 30 minutes, so we use cache-only to avoid blockchain spam
        const response = await fetch(buildBackendUrl(`api/purchase-history/revenue/aggregate?fromTimestamp=${fromTimestamp}&useCache=true`), {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        })

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }

        const data = await response.json()
        const rawPurchases = data.purchases || []

        // AGC: Transform backend format to frontend format
        // AGC: FIX 2025-11-28 - Safe price conversion (handles string/number/BigInt from backend)
        const purchases: Array<{ price: bigint; paymentMethod: string; timestamp: number }> = rawPurchases.map((p: any) => {
          // AGC: Safely convert price to BigInt
          let price: bigint
          if (typeof p.price === 'string') {
            price = BigInt(p.price)
          } else if (typeof p.price === 'number') {
            price = BigInt(Math.floor(p.price))
          } else if (typeof p.price === 'bigint') {
            price = p.price
          } else {
            price = 0n
          }
          
          return {
            price,
            paymentMethod: p.paymentMethod || 'Unknown',
            timestamp: p.timestamp || Date.now(),
          }
        })

        console.log(`✅ [Revenue] Loaded ${purchases.length} purchases from ALL USERS from last Monday (${new Date(lastMondayTimestamp * 1000).toISOString()}) via API`)
        console.log(`📊 [Revenue] NOTE: This is GLOBAL revenue (all users), not personal spending. Personal spending is shown in Purchase History page.`)
        if (purchases.length > 0) {
          console.log(`🔍 [Revenue] Sample purchase: ${formatUnits(purchases[0].price, purchases[0].paymentMethod === 'USDC' ? 6 : 18)} ${purchases[0].paymentMethod}`)
        }
        setAllPurchases(purchases)
      } catch (err: any) {
        console.error('❌ [Revenue] Error fetching purchase revenue:', err)
      }
    }

    fetchAllPurchases()

    // AGC: Refresh every 30 minutes to get latest revenue data (from cache only, no blockchain sync)
    // AGC: Global purchases sync happens max once per 60 minutes or when PurchaseHistory page opens
    // AGC: Revenue doesn't change frequently, so 30 minutes is sufficient for display
    const interval = setInterval(fetchAllPurchases, 30 * 60 * 1000) // AGC: 30 minutes
    return () => clearInterval(interval)
  }, [isConnected, getLastMondayTimestamp])

  // AGC: Calculate total revenue from purchases (convert all to USDC equivalent)
  useEffect(() => {
    if (allPurchases.length === 0) {
      setTotalRevenueUSDC(0)
      return
    }

    // AGC: FIX 2025-11-28 - Fallback asset quotes for testnet (when contract quotes are 0)
    // AGC: Based on: 728 OGB = $0.01 → 72800 OGB per USD → 72800e18 wei per USD
    // AGC: ETH at ~$3333/ETH → 1e18/3333 ≈ 300000000000000 wei per USD
    const FALLBACK_OGB_PER_USD_MICRO = 72800000000000000000000n // 72800e18 - based on 728 OGB = $0.01
    const FALLBACK_ETH_PER_USD_MICRO = 300000000000000n // ~$3333/ETH for testnet estimation

    // AGC: FIX 2025-11-28 - Use fallback values if contract quotes are 0 (common on testnet)
    let ogbPerUsdMicro = FALLBACK_OGB_PER_USD_MICRO
    let ethPerUsdMicro = FALLBACK_ETH_PER_USD_MICRO

    if (assetQuotesData) {
      const quotes = assetQuotesData as [bigint, bigint, bigint, bigint]
      // AGC: Use contract quotes if they're > 0, otherwise use fallback
      if (quotes[0] > 0n) ogbPerUsdMicro = quotes[0]
      if (quotes[1] > 0n) ethPerUsdMicro = quotes[1]
      
      console.log('📊 [Revenue] Asset quotes from contract:', {
        ogb: quotes[0].toString(),
        eth: quotes[1].toString(),
        usingFallback: quotes[0] === 0n || quotes[1] === 0n
      })
    } else {
      console.log('⚠️ [Revenue] Asset quotes not loaded, using fallback values')
    }

    console.log(`🔍 [Revenue] Processing ${allPurchases.length} purchases with quotes:`)
    console.log(`   OGB per USD: ${ogbPerUsdMicro}`)
    console.log(`   ETH per USD: ${ethPerUsdMicro}`)

    let totalUsdc = 0n
    let usdcCount = 0, ethCount = 0, ogbCount = 0, unknownCount = 0

    for (const purchase of allPurchases) {
      const paymentMethod = (purchase.paymentMethod || 'Unknown').trim()
      const methodLower = paymentMethod.toLowerCase()
      
      if (methodLower === 'usdc') {
        // AGC: USDC is already in base units (6 decimals)
        totalUsdc += purchase.price
        usdcCount++
      } else if (methodLower === 'eth') {
        // AGC: Convert ETH (wei) to USDC equivalent with high precision
        // AGC: Use 1e12 extra precision to avoid truncation for small ETH amounts
        // ethAmount (wei) * 1e18 / ethPerUsdMicro = USD value with 18 decimals
        // Then scale to USDC (6 decimals)
        const highPrecisionUsdc = (purchase.price * 1_000_000_000_000_000_000n) / ethPerUsdMicro
        const ethUsdc = highPrecisionUsdc / 1_000_000_000_000n // Scale back to 6 decimals
        totalUsdc += ethUsdc
        ethCount++
        console.log(`   ETH ${formatUnits(purchase.price, 18)} → ${formatUnits(ethUsdc, 6)} USDC (raw: ${purchase.price})`)
      } else if (methodLower === 'ogb') {
        // AGC: Convert OGB (wei) to USDC equivalent with high precision
        // AGC: Use 1e18 extra precision to avoid truncation for small OGB amounts
        const highPrecisionUsdc = (purchase.price * 1_000_000_000_000_000_000n) / ogbPerUsdMicro
        const ogbUsdc = highPrecisionUsdc / 1_000_000_000_000n // Scale back to 6 decimals
        totalUsdc += ogbUsdc
        ogbCount++
        console.log(`   OGB ${formatUnits(purchase.price, 18)} → ${formatUnits(ogbUsdc, 6)} USDC (raw: ${purchase.price})`)
      } else {
        console.warn(`⚠️ [Revenue] Unknown payment method: "${paymentMethod}" (raw value)`)
        unknownCount++
      }
    }

    const totalUsdcNumber = Number(formatUnits(totalUsdc, 6))
    console.log(`✅ [Revenue] RESULT:`)
    console.log(`   USDC purchases: ${usdcCount}`)
    console.log(`   ETH purchases: ${ethCount}`)
    console.log(`   OGB purchases: ${ogbCount}`)
    console.log(`   Unknown: ${unknownCount}`)
    console.log(`   TOTAL: ${totalUsdcNumber} USDC`)
    setTotalRevenueUSDC(totalUsdcNumber)
  }, [allPurchases, assetQuotesData])

  // AGC: Calculate my stake percentage EARLY for earning calculation
  // AGC: FIX 2025-11-28 - Moved calculation before useEffect
  const myStakePercentCalc = totalStaked > 0n && stakingInfo?.stakedBalance
    ? (Number(stakingInfo.stakedBalance) / Number(totalStaked)) * 100
    : 0

  // AGC: Calculate my earning = Total Revenue * My Stake Percentage
  // AGC: FIX 2025-11-28 - Use simple stake % instead of contract time-weighted share
  useEffect(() => {
    if (myStakePercentCalc > 0 && totalRevenueUSDC > 0) {
      const myEarning = (totalRevenueUSDC * myStakePercentCalc) / 100
      setMyEarningUSDC(myEarning)
      console.log(`💰 My earning: ${myEarning.toFixed(4)} USDC (${myStakePercentCalc.toFixed(2)}% of ${totalRevenueUSDC.toFixed(4)})`)
    } else {
      setMyEarningUSDC(0)
    }
  }, [myStakePercentCalc, totalRevenueUSDC])

  // AGC: Update pending rewards
  useEffect(() => {
    if (pendingRewardsData && Array.isArray(pendingRewardsData)) {
      const [ethAmount, ogbAmount, usdcAmount] = pendingRewardsData as [bigint, bigint, bigint]
      setPendingRewards({ eth: ethAmount, ogb: ogbAmount, usdc: usdcAmount })
    } else {
      setPendingRewards(null)
    }
  }, [pendingRewardsData])

  // AGC: Refetch after claim transaction completes
  useEffect(() => {
    if (claimReceipt && claimTxHash) {
      console.log('✅ Claim transaction confirmed, refetching rewards...')
      
      // AGC: Stop coin sound on success
      if (coinSoundIntervalRef.current) {
        clearInterval(coinSoundIntervalRef.current)
        coinSoundIntervalRef.current = null
      }
      
      // AGC: Play congratulations sound on successful claim
      playSoundSafe('/sounds/congratulations.mp3', 0.6)
      
      setTimeout(() => {
        refetchPendingRewards()
        refetchWeekRevenue()
        refetchMyShare()
        setClaimTxHash(null)
        setLoading(false)
      }, 2000)
    }
  }, [claimReceipt, claimTxHash, refetchPendingRewards, refetchWeekRevenue, refetchMyShare])
  
  // AGC: Use already calculated stake percentage (defined earlier for earning calculation)
  const myStakePercent = myStakePercentCalc

  // AGC: Refetch revenue data when week changes
  useEffect(() => {
    if (currentWeek !== undefined) {
      refetchWeekRevenue()
      if (address) {
        refetchMyShare()
        refetchPendingRewards()
      }
    }
  }, [currentWeek, address, refetchWeekRevenue, refetchMyShare, refetchPendingRewards])

  // AGC: Load unstake requests from on-chain StakingManagerV4 contract
  // AGC: V4 Features: 3-day unbonding, 2-of-2 multi-sig + staker voting emergency withdrawal
  // AGC: Contract address from VITE_STAKING_MANAGER_ADDRESS env variable
  useEffect(() => {
    if (!isConnected || !address || !publicClient || STAKING_MANAGER_ADDRESS === '0x0000000000000000000000000000000000000000') return

    const loadUnstakeRequests = async () => {
      try {
        // AGC: Get staking info to know how many unstake requests exist
        const stakingInfo = await publicClient.readContract({
          address: STAKING_MANAGER_ADDRESS,
          abi: STAKING_MANAGER_ABI,
          functionName: 'getStakingInfo',
          args: [address],
        }) as [bigint, bigint, number, bigint]

        const pendingUnstakesCount = Number(stakingInfo[3])
        
        if (pendingUnstakesCount === 0) {
          setUnstakeRequests([])
          return
        }

        // AGC: Fetch all unstake requests on-chain (try up to 20 requests)
        const requests: UnstakeRequestData[] = []
        for (let i = 0; i < Math.min(pendingUnstakesCount + 5, 20); i++) {
          try {
            const request = await publicClient.readContract({
              address: STAKING_MANAGER_ADDRESS,
              abi: STAKING_MANAGER_ABI,
              functionName: 'getUnstakeRequest',
              args: [address, BigInt(i)],
            }) as [bigint, bigint, bigint, boolean]

            const [amount, requestedAt, unlockTime, completed] = request
            
            requests.push({
              amount,
              requestedAt,
              unlockTime,
              completed,
              index: i,
            })
          } catch (error) {
            // AGC: No more requests (index out of bounds)
            break
          }
        }

        // AGC: Filter to only pending (non-completed) requests
        setUnstakeRequests(requests.filter((r) => !r.completed))
      } catch (error) {
        console.error('❌ Error loading unstake requests from on-chain:', error)
        setUnstakeRequests([])
      }
    }

    loadUnstakeRequests()
  }, [address, isConnected, publicClient, refetchStakingInfo])

  // AGC: Refetch staking info after transaction completes
  useEffect(() => {
    if (isPending === false && stakingInfo) {
      const timeout = setTimeout(() => {
        refetchStakingInfo()
        refetchTotalStaked()
        refetchCurrentWeek()
        refetchWeekRevenue()
        refetchMyShare()
        setLoading(false)
      }, 2000)
      return () => clearTimeout(timeout)
    }
  }, [isPending, refetchStakingInfo, refetchTotalStaked, refetchCurrentWeek, refetchWeekRevenue, refetchMyShare])

  if (!isConnected) {
    return (
      <main
        className="pb-[88px] px-3 min-h-screen flex items-center justify-center text-white text-sm"
      >
        <div className="text-slate-300">Please connect your wallet to view staking information.</div>
      </main>
    )
  }

  if (loading && !stakingInfo && !isPending) {
    return (
      <main
        className="pb-[88px] px-3 min-h-screen flex items-center justify-center text-white text-sm"
      >
        <div className="text-slate-300 animate-pulse">Loading staking information…</div>
      </main>
    )
  }

  const stakedOGB = stakingInfo?.stakedBalance ? formatEther(stakingInfo.stakedBalance) : '0'
  const stakingScorePercent = stakingInfo?.stakingScorePercent || 0
  const tier = stakingInfo?.tier || 'NONE'
  const availableOGB = formatEther(ogbBalance || 0n)

  // AGC: Format numbers with specified decimal places (default 3)
  const formatNumber = (value: string | number, decimals: number = 3): string => {
    const num = typeof value === 'string' ? parseFloat(value) : value
    if (isNaN(num)) return '0'
    // AGC: Format with specified decimals, remove trailing zeros
    return parseFloat(num.toFixed(decimals)).toString()
  }

  // AGC: Calculate progress to next tier
  const stakedAmountNum = parseFloat(stakedOGB)
  const progressToMax = tier === 'MAX' ? 100 : Math.min(100, (stakedAmountNum / 10000) * 100)

  // AGC: Handle stake
  const handleStake = async () => {
    if (!address || !stakeAmount || parseFloat(stakeAmount) <= 0) {
      setError('Please enter a valid stake amount')
      return
    }

    try {
      setError(null)
      setLoading(true)
      
      // AGC: Play click sound
      playSoundSafe('/sounds/click.mp3', 0.3)
      
      const txHash = await stake(stakeAmount)
      console.log('✅ Stake transaction sent:', txHash)
      setStakeAmount('')
      
      // AGC: Play coin sound on successful stake
      playSoundSafe('/sounds/coin.wav', 0.5)
      
      setTimeout(() => {
        refetchStakingInfo()
        refetchTotalStaked()
        setLoading(false)
      }, 3000)
    } catch (error: any) {
      console.error('❌ Error staking:', error)
      setError(error.message || 'Failed to stake OGB')
      setLoading(false)
    }
  }

  // AGC: Handle unstake request
  const handleRequestUnstake = async () => {
    if (!address || !unstakeAmount || parseFloat(unstakeAmount) <= 0) {
      setError('Please enter a valid unstake amount')
      return
    }

    if (parseFloat(unstakeAmount) > parseFloat(stakedOGB)) {
      setError('Unstake amount exceeds staked balance')
      return
    }

    try {
      setError(null)
      
      // AGC: Play click sound
      playSoundSafe('/sounds/click.mp3', 0.3)
      
      const txHash = await requestUnstake(unstakeAmount)
      console.log('✅ Unstake request transaction sent:', txHash)
      setUnstakeAmount('')
    } catch (error: any) {
      console.error('❌ Error requesting unstake:', error)
      setError(error.message || 'Failed to request unstake')
    }
  }

  // AGC: Handle max unstake
  const handleMaxUnstake = () => {
    // AGC: Play click sound
    playSoundSafe('/sounds/click.mp3', 0.3)
    setUnstakeAmount(stakedOGB)
  }

  // AGC: Handle complete unstake
  const handleCompleteUnstake = async (requestIndex: number) => {
    if (!address) return

    try {
      setError(null)
      
      // AGC: Play click sound
      playSoundSafe('/sounds/click.mp3', 0.3)
      
      const txHash = await completeUnstake(requestIndex)
      console.log('✅ Complete unstake transaction sent:', txHash)
      
      // AGC: Play coin sound on successful complete
      playSoundSafe('/sounds/coin.wav', 0.5)
      
      setUnstakeRequests((prev) => prev.filter((r) => r.index !== requestIndex))
    } catch (error: any) {
      console.error('❌ Error completing unstake:', error)
      setError(error.message || 'Failed to complete unstake')
    }
  }

  // AGC: Handle claim rewards
  const handleClaimRewards = async () => {
    if (!address || claimableWeek === undefined) {
      setError('Cannot claim: missing address or week data')
      return
    }

    if (!weekFinalized) {
      setError('Week not finalized yet. Wait for Sunday 23:59 UTC or owner to finalize.')
      return
    }

    if (!pendingRewards || (pendingRewards.eth === 0n && pendingRewards.ogb === 0n && pendingRewards.usdc === 0n)) {
      setError('No rewards available to claim for this week')
      return
    }

    try {
      setError(null)
      setLoading(true)
      
      // AGC: Play click sound immediately
      playSoundSafe('/sounds/click.mp3', 0.3)
      
      // AGC: Start playing coin sound repeatedly while claiming
      playSoundSafe('/sounds/coin.wav', 0.5)
      coinSoundIntervalRef.current = setInterval(() => {
        playSoundSafe('/sounds/coin.wav', 0.5)
      }, 800) // Play every 800ms
      
      const hash = await writeContract({
        address: STAKING_REVENUE_SHARE_ADDRESS,
        abi: STAKING_REVENUE_SHARE_ABI,
        functionName: 'claimRewards',
        args: [claimableWeek],
      })
      
      setClaimTxHash(hash)
      console.log('✅ Claim rewards transaction sent:', hash)
    } catch (error: any) {
      console.error('❌ Error claiming rewards:', error)
      setError(error.message || 'Failed to claim rewards')
      setClaimTxHash(null)
      setLoading(false)
      
      // AGC: Stop coin sound on error
      if (coinSoundIntervalRef.current) {
        clearInterval(coinSoundIntervalRef.current)
        coinSoundIntervalRef.current = null
      }
    }
  }

  // AGC: Check if claim button should be enabled
  const canClaim = weekFinalized && 
                   pendingRewards !== null && 
                   (pendingRewards.eth > 0n || pendingRewards.ogb > 0n || pendingRewards.usdc > 0n) &&
                   !isClaimPending &&
                   claimableWeek !== undefined

  // AGC: New stats grid with revenue sharing info (3 cards only)
  const statsGrid = [
    { 
      icon: '💰', 
      label: 'Total Staked', 
      value: formatNumber(formatEther(totalStaked)), 
      unit: 'OGB', 
      accent: 'text-emerald-300' 
    },
    { 
      icon: '📊', 
      label: 'My % of Stake', 
      value: myStakePercent > 0 ? myStakePercent.toFixed(2) : '0', 
      unit: '%', 
      accent: 'text-blue-300' 
    },
    { 
      icon: '💵', 
      label: 'Total Revenue', 
      value: formatNumber(totalRevenueUSDC.toFixed(3), 3), 
      unit: 'USDC', 
      accent: 'text-yellow-300',
      tooltip: 'Total revenue from all users (this week)' // AGC: FIX 2025-11-28 - Clarify this is global revenue, not personal spending
    },
  ] as const

  return (
    <main
      className="pb-[88px] px-3 text-white flex flex-col gap-1.5"
    >
      {/* AGC: Hero Header (No Card) - matching Profile.tsx */}
      <section className="pb-2 border-b border-white/10">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[9px] uppercase tracking-[0.25em] text-slate-500">STAKING</p>
            <h1 className="text-2xl font-bold leading-tight text-white">{formatNumber(stakedOGB)} OGB</h1>
            <p className="text-[11px] text-slate-400">Tier: {tier} • Score: {formatNumber(stakingScorePercent.toString())}%</p>
          </div>
          <div className="text-right">
            <p className="text-[9px] uppercase tracking-[0.25em] text-slate-500">Available</p>
            <p className="text-xl font-bold text-purple-300">{formatNumber(availableOGB)}</p>
          </div>
        </div>
        
        {/* AGC: Progress Bar */}
        <div className="mt-3">
          <div className="flex items-center justify-between text-[10px] text-slate-400 mb-1">
            <span>0 OGB</span>
            <span>500 (Access)</span>
            <span>10,000 (Max)</span>
          </div>
          <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden shadow-inner shadow-black/30">
            <div
              className="h-full rounded-full bg-gradient-to-r from-blue-500 via-cyan-400 to-emerald-400 transition-all duration-500"
              style={{ width: `${progressToMax}%` }}
            />
          </div>
        </div>
      </section>

      {/* AGC: Stats Grid (3 columns) - Revenue Sharing Info */}
      <section className="grid grid-cols-3 gap-1.5 text-[11px]">
        {statsGrid.map((tile) => {
          // AGC: Make Total Revenue clickable to open Purchase History
          const isTotalRevenue = tile.label === 'Total Revenue'
          const TileComponent = isTotalRevenue ? 'button' : 'div'
          const tileProps = isTotalRevenue
            ? {
                onClick: () => {
                  // AGC: Navigate to purchase history - use the correct event name that MainMenu listens for
                  if (typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('mainmenu:navigate-to-purchase-history'))
                  }
                },
                className: 'rounded-xl bg-gradient-to-br from-slate-950 to-slate-900 border border-white/5 px-2 py-2 shadow-[0_8px_20px_rgba(0,0,0,0.45)] hover:bg-slate-900/90 transition-colors cursor-pointer',
                title: 'Total revenue from ALL users (this week). Click to view purchase history.', // AGC: FIX 2025-11-28 - Clarify this is global revenue
              }
            : {
                className: 'rounded-xl bg-gradient-to-br from-slate-950 to-slate-900 border border-white/5 px-2 py-2 shadow-[0_8px_20px_rgba(0,0,0,0.45)] hover:bg-slate-900/90 transition-colors',
              }

          return (
            <TileComponent
              key={tile.label}
              {...tileProps}
            >
            <div className="flex items-center gap-1 text-[10px] text-slate-300 uppercase tracking-wide">
              <span>{tile.icon}</span>
              {tile.label}
            </div>
            <div className={`text-2xl font-extrabold mt-0.5 ${tile.accent}`}>
              {tile.value}
            </div>
            <div className="text-[9px] text-slate-500 flex items-center justify-between">
              <span>{tile.unit}</span>
              {/* AGC: BaseScan link for Total Staked - Points to StakingManagerV4 */}
              {tile.label === 'Total Staked' && STAKING_MANAGER_ADDRESS !== '0x0000000000000000000000000000000000000000' && (
                <a
                  href={`https://sepolia.basescan.org/address/${STAKING_MANAGER_ADDRESS}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-400 hover:text-blue-300 underline text-[8px]"
                  onClick={(e) => e.stopPropagation()}
                  title={`View StakingManagerV4 on BaseScan (${STAKING_MANAGER_ADDRESS.slice(0, 6)}...${STAKING_MANAGER_ADDRESS.slice(-4)})`}
                >
                  BaseScan
                </a>
              )}
            </div>
          </TileComponent>
        )
        })}
      </section>

      {/* AGC: Claim Rewards Section */}
      <section className="pb-2 border-b border-white/10">
        <div className="flex items-center justify-between mb-2">
          <div>
            <p className="text-[9px] uppercase tracking-[0.25em] text-slate-500">MY EARNING</p>
            <p className="text-sm text-purple-300 font-semibold">{formatNumber(myEarningUSDC.toFixed(2))} USDC</p>
            {pendingRewards && (pendingRewards.eth > 0n || pendingRewards.ogb > 0n || pendingRewards.usdc > 0n) && (
              <p className="text-[10px] text-slate-400 mt-0.5">
                {pendingRewards.eth > 0n && `${formatNumber(formatEther(pendingRewards.eth))} ETH `}
                {pendingRewards.ogb > 0n && `${formatNumber(formatEther(pendingRewards.ogb))} OGB `}
                {pendingRewards.usdc > 0n && `${formatNumber(formatUnits(pendingRewards.usdc, 6))} USDC`}
              </p>
            )}
          </div>
          <div className="relative">
            <button
              onClick={canClaim ? handleClaimRewards : () => setShowNextPaymentTooltip(!showNextPaymentTooltip)}
              onMouseEnter={() => !canClaim && setShowNextPaymentTooltip(true)}
              onMouseLeave={() => setShowNextPaymentTooltip(false)}
              disabled={!canClaim && !isClaimPending}
              className={`px-4 py-2 rounded-lg font-semibold text-sm transition ${
                canClaim && !isClaimPending
                  ? 'bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 text-white hover:scale-[1.02]'
                  : 'bg-slate-800 text-slate-500 cursor-pointer'
              }`}
            >
              {isClaimPending ? 'Claiming...' : canClaim ? '🎁 Claim Rewards' : weekFinalized ? 'No Rewards' : 'Not Ready'}
            </button>
            {/* AGC: Tooltip showing next payment date/time */}
            {showNextPaymentTooltip && !canClaim && (
              <div className="absolute right-0 top-full mt-2 w-64 p-3 bg-slate-900 border border-slate-700 rounded-lg shadow-xl z-50">
                <p className="text-xs font-semibold text-emerald-400 mb-1">Next Payment Date</p>
                <p className="text-xs text-slate-300">{nextPaymentDateStr}</p>
                {!weekFinalized && (
                  <p className="text-[10px] text-slate-500 mt-2">Week will be finalized at this time, or earlier by admin.</p>
                )}
              </div>
            )}
          </div>
        </div>
        {!weekFinalized && claimableWeek !== undefined && (
          <p className="text-[9px] text-slate-500">Week {claimableWeek.toString()} not finalized yet. Wait for Sunday 23:59 UTC or owner to finalize.</p>
        )}
        {weekFinalized && pendingRewards && (pendingRewards.eth === 0n && pendingRewards.ogb === 0n && pendingRewards.usdc === 0n) && (
          <p className="text-[9px] text-slate-500">No rewards available for this week.</p>
        )}
      </section>

      {/* AGC: Tier Benefits */}
      <section className="pb-2 border-b border-white/10">
        <p className="text-[9px] uppercase tracking-[0.25em] text-slate-500 mb-2">TIER BENEFITS</p>
        <div className="grid grid-cols-2 gap-2 text-[11px]">
          <div className={`flex items-center gap-1.5 ${tier !== 'NONE' ? 'text-emerald-300' : 'text-slate-500'}`}>
            <Shield className="w-4 h-4" />
            <span>{tier !== 'NONE' ? '✓' : '✗'} Quest Access</span>
          </div>
          <div className={`flex items-center gap-1.5 ${tier !== 'NONE' ? 'text-emerald-300' : 'text-slate-500'}`}>
            <ShoppingBag className="w-4 h-4" />
            <span>{tier !== 'NONE' ? '✓' : '✗'} 50% Discount</span>
          </div>
          <div className={`flex items-center gap-1.5 ${tier === 'MAX' ? 'text-yellow-300' : 'text-slate-500'}`}>
            <TrendingUp className="w-4 h-4" />
            <span>{tier === 'MAX' ? '✓' : '✗'} Max Score</span>
          </div>
          <div className="flex items-center gap-1.5 text-slate-400">
            <Zap className="w-4 h-4" />
            <span>3d Unbond</span>
          </div>
        </div>
      </section>

      {/* AGC: Error Display */}
      {(error || stakingHookError) && (
        <div className="bg-red-900/40 border border-red-500 rounded-lg p-3 text-red-200 text-sm">
          ⚠️ {error || stakingHookError}
          {stakingManagerAddress === '0x0000000000000000000000000000000000000000' && (
            <div className="mt-2 text-xs">StakingManager address not configured. Check VITE_STAKING_MANAGER_ADDRESS in env.public</div>
          )}
        </div>
      )}

      {/* AGC: Stake/Unstake Tabs */}
      <section className="pb-2 border-b border-white/10">
        <div className="flex gap-2 mb-3">
          <button
            onClick={() => {
              playSoundSafe('/sounds/click.mp3', 0.3)
              setActiveTab('stake')
            }}
            className={`flex-1 px-4 py-2 rounded-lg font-semibold text-sm transition ${
              activeTab === 'stake'
                ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white'
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            Stake
          </button>
          <button
            onClick={() => {
              playSoundSafe('/sounds/click.mp3', 0.3)
              setActiveTab('unstake')
            }}
            className={`flex-1 px-4 py-2 rounded-lg font-semibold text-sm transition ${
              activeTab === 'unstake'
                ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white'
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            Unstake
          </button>
        </div>

        {/* AGC: Stake Tab */}
        {activeTab === 'stake' && (
          <div>
            <p className="text-[9px] uppercase tracking-[0.25em] text-slate-500 mb-2">STAKE OGB</p>
            <div className="flex gap-2">
              <input
                type="number"
                value={stakeAmount}
                onChange={(e) => setStakeAmount(e.target.value)}
                placeholder="Amount"
                className="flex-1 px-3 py-2 bg-slate-900/60 border border-white/10 rounded-lg text-white text-sm"
                disabled={isPending}
              />
              <button
                onClick={handleStake}
                disabled={isPending || !stakeAmount || parseFloat(stakeAmount) <= 0}
                className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:scale-[1.01] transition"
              >
                {isPending ? '...' : 'Stake'}
              </button>
            </div>
            {isPending && (
              <p className="text-[10px] text-slate-400 mt-1">⏳ Waiting for transaction confirmation...</p>
            )}
          </div>
        )}

        {/* AGC: Unstake Tab */}
        {activeTab === 'unstake' && (
          <div>
            <p className="text-[9px] uppercase tracking-[0.25em] text-slate-500 mb-2">REQUEST UNSTAKE</p>
            
            {/* AGC: Lock info for airdrop recipients - informational only, allows unstake attempt */}
            {lockInfo?.isLocked && (
              <div className="mb-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <p className="text-amber-400 text-xs font-semibold flex items-center gap-2">
                  ⚠️ Airdrop Lock Period Active
                </p>
                <p className="text-amber-300/80 text-[10px] mt-1">
                  Airdropped tokens have a 60-day lock period.
                  {lockInfo.daysRemaining > 0 && (
                    <span className="font-semibold"> {lockInfo.daysRemaining} days remaining</span>
                  )}
                  {lockInfo.unlockDate && (
                    <span className="block mt-0.5 text-amber-300/60">
                      Unlocks: {lockInfo.unlockDate.toLocaleDateString()} at {lockInfo.unlockDate.toLocaleTimeString()}
                    </span>
                  )}
                </p>
                <p className="text-amber-200/60 text-[9px] mt-1">
                  💡 You can still unstake any tokens staked outside the airdrop.
                </p>
              </div>
            )}
            
            <div className="flex gap-2">
              <input
                type="number"
                value={unstakeAmount}
                onChange={(e) => setUnstakeAmount(e.target.value)}
                placeholder="Amount"
                className="flex-1 px-3 py-2 bg-slate-900/60 border border-white/10 rounded-lg text-white text-sm"
                disabled={isPending}
              />
              <button
                onClick={handleMaxUnstake}
                disabled={isPending || parseFloat(stakedOGB) <= 0}
                className="px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                Max
              </button>
              <button
                onClick={handleRequestUnstake}
                disabled={isPending || !unstakeAmount || parseFloat(unstakeAmount) <= 0}
                className="px-4 py-2 bg-gradient-to-r from-orange-500 to-red-500 rounded-lg font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:scale-[1.01] transition"
              >
                {isPending ? '...' : 'Unstake'}
              </button>
            </div>
            <p className="text-[9px] text-slate-500 mt-1">
              3-day unbonding period applies
            </p>
          </div>
        )}
      </section>

      {/* AGC: Pending Unstake Requests */}
      {unstakeRequests.length > 0 && (
        <section className="pb-2 border-b border-white/10">
          <p className="text-[9px] uppercase tracking-[0.25em] text-slate-500 mb-2">PENDING REQUESTS</p>
          <div className="space-y-2">
            {unstakeRequests.map((request) => {
              const now = BigInt(Math.floor(Date.now() / 1000))
              const canComplete = now >= request.unlockTime
              const timeRemaining = request.unlockTime > now ? request.unlockTime - now : 0n
              const hoursRemaining = Number(timeRemaining) / 3600
              const daysRemaining = Math.floor(hoursRemaining / 24)
              const hours = Math.floor(hoursRemaining % 24)

              return (
                <div key={request.index} className="p-3 bg-slate-900/60 border border-white/10 rounded-lg">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-bold text-white">{formatNumber(formatEther(request.amount))} OGB</p>
                      {canComplete ? (
                        <p className="text-sm text-emerald-400">Ready to withdraw</p>
                      ) : (
                        <p className="text-sm text-slate-400">
                          Unlocks in {daysRemaining}d {hours}h
                        </p>
                      )}
                    </div>
                    {canComplete && (
                      <button
                        onClick={() => handleCompleteUnstake(request.index)}
                        disabled={isPending}
                        className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 rounded-lg font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Complete
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </section>
      )}
    </main>
  )
}

